import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import ActionsDialog from '../../../components/ActionsDialog/ActionsDialog';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useCompanyLinkDeleteMutation } from '../../requests/mutations';
import { CompanyLinkData } from '../CompanyLinksTable/CompanyLinksTable';

export enum LinkDialogAction {
  Delete,
}

interface CompanyLinkActionDialogProps {
  open: boolean;
  onClose: () => void;
  companyLink: CompanyLinkData | undefined;
  onDeleteAction: () => void;
}

interface DialogCloseButtonProps {
  onClose: () => void;
}

export function DialogCloseButton(props: DialogCloseButtonProps) {
  const { onClose } = props;
  const { t } = useTranslation();
  return (
    <IconButton
      aria-label={t('buttonText.close')}
      onClick={onClose}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
      }}
    >
      <CloseIcon />
    </IconButton>
  );
}

function CompanyLinkActionDialog(props: CompanyLinkActionDialogProps) {
  const { open, onClose, companyLink, onDeleteAction } = props;
  const { t } = useTranslation();
  const { setSnackbarState } = useSnackbar();

  const deleteLinkMutation = useCompanyLinkDeleteMutation();

  const handleDeleteCompanyLink = () => {
    if (companyLink !== undefined) {
      deleteLinkMutation.mutate(
        { linkId: companyLink.id },
        {
          onSuccess: () => {
            setSnackbarState({ open: true, message: t('admin.editLinksPage.linkDeleted'), color: 'success' });
            onClose();
            onDeleteAction();
          },
          onError: () => {
            setSnackbarState({ open: true, message: t('admin.editLinksPage.errorDeletingLink'), color: 'error' });
            onClose();
          },
        }
      );
    }
  };

  const getCancelMessage = (companyLinkData: CompanyLinkData | undefined) =>
    `${t('admin.editLinksPage.deleteLinkConfirmation', { link: companyLinkData?.displayName })}`;

  return (
    <ActionsDialog
      open={open}
      dataTestId='company-link-action-dialog'
      dialogTitle={t('admin.editLinksPage.deleteLinkTitle', { link: companyLink?.displayName })}
      closeButtonText={t('buttonText.cancel')}
      closeButtonVariant='text'
      actionButtonText={t('buttonText.delete')}
      actionButtonColor='error'
      onClose={onClose}
      onActionClick={handleDeleteCompanyLink}
      dialogContentText={getCancelMessage(companyLink)}
    />
  );
}

export default CompanyLinkActionDialog;
